import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { koreaDate } from '@chagok/domain';
import { api } from './api/client';
import type { Bootstrap, Session, TransactionKind } from './api/types';
import { thisMonth, type Period } from './lib/period';

export type Page = 'dashboard' | 'history' | 'recurring' | 'budgets' | 'insights' | 'settings' | 'data';
export const PAGE_PATHS: Record<Page, string> = {
  dashboard: '/',
  history: '/history',
  recurring: '/recurring',
  budgets: '/budgets',
  insights: '/insights',
  settings: '/settings',
  data: '/data'
};

export interface HistoryFilter {
  kind?: TransactionKind | '';
  categoryId?: string;
  subcategoryId?: string;
  paymentMethodId?: string;
  q?: string;
}

interface AppState {
  session: Session;
  catalog: Bootstrap;
  reloadCatalog: () => Promise<void>;
  period: Period;
  setPeriod: (p: Period) => void;
  page: Page;
  navigate: (page: Page, filter?: HistoryFilter) => void;
  historyFilter: HistoryFilter;
  setHistoryFilter: (f: HistoryFilter) => void;
  /** 쓰기 성공 후 올려서 화면들이 다시 읽게 한다. */
  dataVersion: number;
  changed: () => void;
  toast: (message: string) => void;
  openEntry: (options?: EntryRequest) => void;
  entryRequest: EntryRequest | null;
  closeEntry: () => void;
}

export interface EntryRequest {
  kind?: 'income' | 'expense' | 'transfer';
  /** 수정할 기록 */
  edit?: import('./api/types').Transaction;
  /** 환불할 원래 지출 */
  refundOf?: import('./api/types').Transaction;
}

const Ctx = createContext<AppState | null>(null);
export const useApp = () => {
  const value = useContext(Ctx);
  if (!value) throw new Error('AppProvider 밖에서 사용했습니다.');
  return value;
};

function pageFromPath(path: string): Page {
  const found = (Object.entries(PAGE_PATHS) as [Page, string][]).find(([, p]) => p === path);
  return found ? found[0] : 'dashboard';
}

export function AppProvider({ session, initialCatalog, children }: { session: Session; initialCatalog: Bootstrap; children: ReactNode }) {
  const [catalog, setCatalog] = useState(initialCatalog);
  const [period, setPeriod] = useState<Period>(() => thisMonth(initialCatalog.serverDate || koreaDate()));
  const [page, setPage] = useState<Page>(() => pageFromPath(location.pathname));
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>({});
  const [dataVersion, setDataVersion] = useState(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [entryRequest, setEntryRequest] = useState<EntryRequest | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onPop = () => setPage(pageFromPath(location.pathname));
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);

  const reloadCatalog = useCallback(async () => {
    const res = await api.get<{ data: Bootstrap }>('/bootstrap');
    setCatalog(res.data);
  }, []);

  const value = useMemo<AppState>(
    () => ({
      session,
      catalog,
      reloadCatalog,
      period,
      setPeriod,
      page,
      navigate: (next, filter) => {
        if (filter) setHistoryFilter(filter);
        if (PAGE_PATHS[next] !== location.pathname) history.pushState(null, '', PAGE_PATHS[next]);
        setPage(next);
        scrollTo({ top: 0 });
      },
      historyFilter,
      setHistoryFilter,
      dataVersion,
      changed: () => setDataVersion((v) => v + 1),
      toast: (message) => {
        setToastMessage(message);
        clearTimeout(toastTimer.current);
        toastTimer.current = window.setTimeout(() => setToastMessage(null), 2800);
      },
      openEntry: (options = {}) => setEntryRequest(options),
      entryRequest,
      closeEntry: () => setEntryRequest(null)
    }),
    [session, catalog, reloadCatalog, period, page, historyFilter, dataVersion, entryRequest]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toast" role="status" hidden={!toastMessage}>
        {toastMessage}
      </div>
    </Ctx.Provider>
  );
}
